import { useState, useEffect, useCallback } from "react";
import {
  fetchUserFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  removeFolderFromLeads,
  updateFolderColor,
  Folder
} from "@/services/foldersService";
import { useAuth } from "@/contexts/AuthContext";

export function useFolders(targetUserId?: string) {
  const { currentUser } = useAuth();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Determine which user's folders to load
  // If targetUserId is provided (e.g., admin viewing another user), use that
  // Otherwise use the current user's ID
  const userId = targetUserId || currentUser?.id;
  const tenantId = currentUser?.tenantId || "legacy";

  const loadFolders = useCallback(async () => {
    if (!userId) {
      setFolders([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const userFolders = await fetchUserFolders(userId, tenantId);
      setFolders(userFolders);
    } catch (err) {
      setError(err as Error);
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, tenantId]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const handleCreateFolder = useCallback(async (name: string, color?: string) => {
    if (!currentUser) return null;

    const newFolder = await createFolder(
      name,
      currentUser.id,
      currentUser.tenantId,
      color
    );

    if (newFolder) {
      setFolders(prev => [...prev, newFolder]);
    }

    return newFolder;
  }, [currentUser]);

  const handleRenameFolder = useCallback(async (folderId: string, newName: string) => {
    const success = await renameFolder(folderId, newName);

    if (success) {
      setFolders(prev =>
        prev.map(f =>
          f.id === folderId
            ? { ...f, name: newName, updatedAt: new Date().toISOString() }
            : f
        )
      );
    }

    return success;
  }, []);

  const handleDeleteFolder = useCallback(async (folderId: string) => {
    if (!currentUser) return false;

    // First remove folder assignment from all leads
    await removeFolderFromLeads(folderId, currentUser.tenantId);

    const success = await deleteFolder(folderId);

    if (success) {
      setFolders(prev => prev.filter(f => f.id !== folderId));
    }

    return success;
  }, [currentUser]);

  const handleUpdateFolderColor = useCallback(async (folderId: string, color: string) => {
    const success = await updateFolderColor(folderId, color);

    if (success) {
      setFolders(prev =>
        prev.map(f =>
          f.id === folderId
            ? { ...f, color, updatedAt: new Date().toISOString() }
            : f
        )
      );
    }

    return success;
  }, []);

  const refetch = useCallback(() => {
    return loadFolders();
  }, [loadFolders]);

  return {
    folders,
    isLoading,
    error,
    refetch,
    setFolders,
    createFolder: handleCreateFolder,
    renameFolder: handleRenameFolder,
    deleteFolder: handleDeleteFolder,
    updateFolderColor: handleUpdateFolderColor
  };
}
